import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faListAlt,
  faCalendarDay,
  faFileAlt,
  faUsers,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons";

const Home = () => {
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth.role?.toUpperCase() === "ADMIN";

  const quickLinks = [
    {
      title: "Daily Trades",
      description: "View today's trading activity",
      icon: faCalendarDay,
      path: "/trade_app/dailyTrades",
      color: "#06b6d4",
    },
    {
      title: "Trade List",
      description: "Browse all trade history",
      icon: faListAlt,
      path: "/trade_app/trades",
      color: "#3b82f6",
    },
    {
      title: "Chart",
      description: "Analyze market charts",
      icon: faChartLine,
      path: "/trade_app/Chart",
      color: "#10b981",
    },
    {
      title: "MTM Report",
      description: "Mark-to-market analysis",
      icon: faFileAlt,
      path: "/trade_app/mtmReport",
      color: "#f59e0b",
    },
  ];

  const adminLinks = [
    {
      title: "Back Test Report",
      description: "Run and analyze backtests",
      icon: faFileAlt,
      path: "/trade_app/report",
      color: "#8b5cf6",
    },
    {
      title: "User Management",
      description: "Manage platform users",
      icon: faUsers,
      path: "/trade_app/users",
      color: "#ec4899",
    },
    {
      title: "Data Tools",
      description: "Compare market data",
      icon: faDatabase,
      path: "/trade_app/marketDataCompare",
      color: "#14b8a6",
    },
  ];

  return (
    <div className="home-container">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-content">
          <h1 className="welcome-title">
            Welcome back, <span className="text-gradient">{auth.username || "User"}</span>
          </h1>
          <p className="welcome-subtitle">
            {isAdmin ? "You have administrator access to all platform features." : "Here's an overview of your trading dashboard."}
          </p>
        </div>
        
        <div className="user-card">
          <div className="user-avatar">
            {auth.username?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="user-info">
            <div className="user-name">{auth.username}</div>
            <div className="user-email">{auth.email}</div>
            <div className={`user-role ${isAdmin ? 'admin' : 'user'}`}>
              {isAdmin ? "Administrator" : "User"}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="section">
        <h2 className="section-title">Quick Access</h2>
        <div className="links-grid">
          {quickLinks.map((link, index) => (
            <Link to={link.path} key={index} className="link-card" style={{"--card-color": link.color}}>
              <div className="link-icon" style={{background: `linear-gradient(135deg, ${link.color}20 0%, ${link.color}10 100%)`}}>
                <FontAwesomeIcon icon={link.icon} style={{color: link.color}} />
              </div>
              <div className="link-content">
                <h3>{link.title}</h3>
                <p>{link.description}</p>
              </div>
              <div className="link-arrow">→</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Admin Section */}
      {isAdmin && (
        <div className="section">
          <h2 className="section-title">
            <span className="admin-badge">Admin</span>
            Management Tools
          </h2>
          <div className="links-grid">
            {adminLinks.map((link, index) => (
              <Link to={link.path} key={index} className="link-card admin-card" style={{"--card-color": link.color}}>
                <div className="link-icon" style={{background: `linear-gradient(135deg, ${link.color}20 0%, ${link.color}10 100%)`}}>
                  <FontAwesomeIcon icon={link.icon} style={{color: link.color}} />
                </div>
                <div className="link-content">
                  <h3>{link.title}</h3>
                  <p>{link.description}</p>
                </div>
                <div className="link-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .home-container {
          padding: 20px 0;
          max-width: 1200px;
          margin: 0 auto;
        }

        .welcome-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid #334155;
          border-radius: 24px;
          padding: 40px;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 24px;
        }

        .welcome-title {
          font-size: 32px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 8px;
        }

        .text-gradient {
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .welcome-subtitle {
          color: #94a3b8;
          font-size: 16px;
          margin: 0;
        }

        .user-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          border-radius: 16px;
          padding: 16px 24px;
        }

        .user-avatar {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 700;
        }

        .user-name {
          font-size: 18px;
          font-weight: 600;
          color: #f1f5f9;
        }

        .user-email {
          font-size: 13px;
          color: #64748b;
        }

        .user-role {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 12px;
          margin-top: 4px;
          text-transform: uppercase;
        }

        .user-role.admin {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .user-role.user {
          background: rgba(6, 182, 212, 0.2);
          color: #06b6d4;
        }

        .section {
          margin-bottom: 40px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
          color: #f1f5f9;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-badge {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          font-size: 11px;
          padding: 4px 10px;
          border-radius: 6px;
          text-transform: uppercase;
        }

        .links-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .link-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid #334155;
          border-radius: 16px;
          padding: 20px;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .link-card:hover {
          transform: translateY(-4px);
          border-color: var(--card-color);
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.4);
          text-decoration: none;
        }

        .link-icon {
          width: 52px;
          height: 52px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }

        .link-content {
          flex: 1;
        }

        .link-content h3 {
          font-size: 16px;
          font-weight: 600;
          color: #f1f5f9;
          margin: 0 0 4px 0;
        }

        .link-content p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .link-arrow {
          font-size: 20px;
          color: #64748b;
          transition: all 0.3s ease;
        }

        .link-card:hover .link-arrow {
          color: var(--card-color);
          transform: translateX(4px);
        }

        @media (max-width: 768px) {
          .welcome-section {
            padding: 24px;
            flex-direction: column;
            align-items: flex-start;
          }

          .welcome-title {
            font-size: 24px;
          }

          .user-card {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
